import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true };
import { Toaster } from 'react-hot-toast';

import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { OrderProvider } from './context/OrderContext';

import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Contact = lazy(() => import('./pages/Contact'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Booking = lazy(() => import('./pages/Booking'));
const Cart = lazy(() => import('./pages/Cart'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const Staff = lazy(() => import('./pages/Staff'));
const Salon = lazy(() => import('./pages/Salon'));

const PageLoader = () => (
  <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
    <div className="text-center">
      <LoadingSpinner size="large" />
      <p className="mt-4 text-gray-600 animate-pulse">Caricamento...</p>
    </div>
  </div>
);

function App() {
  return (
    <Router future={ROUTER_FUTURE}>
      <AuthProvider>
        <CartProvider>
          <OrderProvider>
            <div className="min-h-screen flex flex-col bg-gray-50">
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: { background: '#363636', color: '#fff', borderRadius: '12px', padding: '16px' },
                  success: { duration: 3000, style: { background: '#10B981' }, iconTheme: { primary: '#fff', secondary: '#10B981' } },
                  error: { duration: 4000, style: { background: '#EF4444' }, iconTheme: { primary: '#fff', secondary: '#EF4444' } },
                  loading: { duration: 2000, style: { background: '#F59E0B' } },
                }}
              />

              <Header />

              <main className="flex-grow">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* ── Public Routes ─────────────────────────────────── */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/product/:id" element={<ProductDetail />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/services/:id" element={<ServiceDetail />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/staff" element={<Staff />} />
                    <Route path="/salon" element={<Salon />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/booking" element={<Booking />} />

                    {/* ── Order Tracking ────────────────────────────────── */}
                    <Route path="/track-order" element={<TrackOrderPage />} />
                    <Route path="/tracking/:orderId" element={<OrderTracking />} />

                    {/* ── Authentication ────────────────────────────────── */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />

                    {/* ── Protected Routes ──────────────────────────────── */}
                    <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
                    <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

                    {/* ── Static Pages ──────────────────────────────────── */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/shipping" element={<ShippingPolicy />} />

                    {/* ── Admin ─────────────────────────────────────────── */}
                    <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

                    {/* ── Redirect URL italiani → inglesi ───────────────── */}
                    <Route path="/prodotti" element={<Navigate to="/products" replace />} />
                    <Route path="/prodotti/:id" element={<Navigate to="/products" replace />} />
                    <Route path="/contatti" element={<Navigate to="/contact" replace />} />
                    <Route path="/chi-siamo" element={<Navigate to="/about" replace />} />
                    <Route path="/carrello" element={<Navigate to="/cart" replace />} />
                    <Route path="/prenotazione" element={<Navigate to="/booking" replace />} />
                    <Route path="/servizi" element={<Navigate to="/services" replace />} />
                    <Route path="/galleria" element={<Navigate to="/gallery" replace />} />
                    <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                    <Route path="/spedizione" element={<Navigate to="/shipping" replace />} />
                    <Route path="/termini" element={<Navigate to="/terms" replace />} />

                    {/* ── 404 ───────────────────────────────────────────── */}
                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </Suspense>
              </main>

              <Footer />
            </div>
          </OrderProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;